import { useEffect, useState } from "react";
import { XIcon } from "@heroicons/react/outline";

const CompanyFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  mode = "create",
  initialData = null,
}) => {
  const [form, setForm] = useState({
    name: "",
    reg_no: "",
    vat_no: "",
    accountant_name: "",
    accountant_phone: "",
    company_id: "",
    euid: "",
    addressLine1: "",
    addressLine2: "",
    addressLine3: "",
    city: "",
    county: "",
    country: "",
    postcode: "",
  });

  useEffect(() => {
    if (mode === "edit" && initialData) {
      setForm({
        name: initialData.name || "",
        reg_no: initialData.reg_no || "",
        vat_no: initialData.vat_no || "",
        accountant_name: initialData.accountant_name || "",
        accountant_phone: initialData.accountant_phone || "",
        company_id: initialData.company_id || "",
        euid: initialData.euid || "",
        addressLine1: initialData.address?.address_1 || "",
        addressLine2: initialData.address?.address_2 || "",
        addressLine3: initialData.address?.address_3 || "",
        city: initialData.address?.city || "",
        county: initialData.address?.county || "",
        country: initialData.address?.country || "",
        postcode: initialData.address?.postcode || "",
      });
    }

    if (mode === "create") {
      setForm({
        name: "",
        reg_no: "",
        vat_no: "",
        accountant_name: "",
        accountant_phone: "",
        company_id: "",
        euid: "",
        addressLine1: "",
        addressLine2: "",
        addressLine3: "",
        city: "",
        county: "",
        country: "",
        postcode: "",
      });
    }
  }, [mode, initialData]);

  if (!isOpen) return null;

  const handleChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSubmit = () => {
    onSubmit(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-lg bg-slate-900 border border-white/10 rounded-xl shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white">
            {mode === "edit" ? "Edit Company" : "Add Company"}
          </h2>

          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-sm text-slate-300 mb-1">
              Company Name
            </label>
            <input
              type="text"
              placeholder="company name"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Reg No
              </label>
              <input
                type="text"
                placeholder="Reg No"
                value={form.reg_no}
                onChange={(e) => handleChange("reg_no", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-1">
                VAT No
              </label>
              <input
                type="text"
                placeholder="VAT No"
                value={form.vat_no}
                onChange={(e) => handleChange("vat_no", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Accountant Name
              </label>
              <input
                type="text"
                placeholder="Accountant Name"
                value={form.accountant_name}
                onChange={(e) =>
                  handleChange("accountant_name", e.target.value)
                }
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Accountant Phone
              </label>
              <input
                type="text"
                placeholder="Accountant Phone"
                value={form.accountant_phone}
                onChange={(e) =>
                  handleChange("accountant_phone", e.target.value)
                }
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Company Id
              </label>
              <input
                type="text"
                placeholder="Company Id"
                value={form.company_id}
                onChange={(e) => handleChange("company_id", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-1">EUID</label>
              <input
                type="text"
                placeholder="EUID"
                value={form.euid}
                onChange={(e) => handleChange("euid", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>
          </div>

          <div>
            <h3 className="block text-sm text-slate-300 mb-1">Address</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                placeholder="Address Line 1"
                value={form.addressLine1}
                onChange={(e) =>
                  handleChange("addressLine1", e.target.value)
                }
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="Address Line 2"
                value={form.addressLine2}
                onChange={(e) =>
                  handleChange("addressLine2", e.target.value)
                }
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="Address Line 3"
                value={form.addressLine3}
                onChange={(e) =>
                  handleChange("addressLine3", e.target.value)
                }
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="City"
                value={form.city}
                onChange={(e) => handleChange("city", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="County"
                value={form.county}
                onChange={(e) => handleChange("county", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="Country"
                value={form.country}
                onChange={(e) => handleChange("country", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="Postcode"
                value={form.postcode}
                onChange={(e) => handleChange("postcode", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg text-slate-300 hover:bg-slate-800 transition"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`px-4 py-2 text-sm rounded-lg text-white font-medium transition
              ${
                loading
                  ? "bg-pink-400 cursor-not-allowed"
                  : "bg-pink-600 hover:bg-pink-700"
              }
            `}
          >
            {loading
              ? "Saving..."
              : mode === "edit"
                ? "Update Company"
                : "Create Company"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompanyFormModal;
